import { CSSProperties, useMemo } from "react";
import { GapSize, LayoutUtilProps } from "../../types";
import { useBreakpoint } from "../useBreakpoint";
import { sizeToToken } from "../../internal/functions";

const LAYOUT_PROP_ARR = [
  "gridTemplateColumns",
  "gridTemplateRows",
  "gridTemplateAreas",
  "gridAutoColumns",
  "gridAutoRows",
  "gridArea",
  "gridColumn",
  "gridRow",
  "gridColumnStart",
  "gridColumnEnd",
  "gridRowStart",
  "gridRowEnd",
  "flex",
  "flexDirection",
  "flexGrow",
  "flexShrink",
  "flexBasis",
  "flexWrap",
  "order",
  "justifySelf",
  "justifyItems",
  "justifyContent",
  "alignSelf",
  "alignItems",
  "alignContent",
  "placeItems",
  "placeContent",
  "placeSelf",
  "wrap",
  "gap",
  "rowGap",
  "columnGap",
];

const RESPONSIVE_SIZES_ARR = ["xs", "sm", "md", "lg", "xl", "xxl"];

/**
 * Layout CSS variables type excluding responsive properties
 */
type LayoutCSSVariables = Omit<LayoutUtilProps, keyof ResponsiveLayout>;

/**
 * Responsive layout configuration type
 */
type ResponsiveLayout = {
  /**
   * Base layout properties
   */
  base?: LayoutUtilProps;
  /**
   * Extra small breakpoint layout properties
   */
  xs?: LayoutUtilProps;
  /**
   * Small breakpoint layout properties
   */
  sm?: LayoutUtilProps;
  /**
   * Medium breakpoint layout properties
   */
  md?: LayoutUtilProps;
  /**
   * Large breakpoint layout properties
   */
  lg?: LayoutUtilProps;
  /**
   * Extra large breakpoint layout properties
   */
  xl?: LayoutUtilProps;
  /**
   * Extra extra large breakpoint layout properties
   */
  xxl?: LayoutUtilProps;
};

/**
 * Custom hook for managing responsive layout properties and CSS styles.
 *
 * Features:
 * - Separates layout properties from component properties
 * - Provides responsive breakpoint support for all layout properties
 * - Automatically converts gap sizes to CSS tokens
 * - Uses breakpoint cascade for responsive values
 * - Optimized with useMemo for performance
 * - Supports all CSS Grid and Flexbox properties
 * - Handles responsive size inheritance (larger breakpoints inherit from smaller ones)
 * - Returns both layout styles and filtered component props
 *
 * @param props - Object containing layout and component properties
 * @returns Object containing layout styles and filtered component props
 */
export function useLayoutPropsUtil<T extends ResponsiveLayout>(
  props: T,
): { layoutStyles?: CSSProperties; componentProps: T } {
  const baseSizeProps = Object.fromEntries(
    Object.entries(props).filter(([key, _val]) =>
      LAYOUT_PROP_ARR.includes(key),
    ),
  );

  const responsiveSizeProps = Object.fromEntries(
    Object.entries(props).filter(([key, _val]) =>
      RESPONSIVE_SIZES_ARR.includes(key),
    ),
  );

  const sizeProps: ResponsiveLayout = useMemo(
    () => ({
      ...responsiveSizeProps,
      base: baseSizeProps,
    }),
    [baseSizeProps, responsiveSizeProps],
  );

  const componentProps = Object.fromEntries(
    Object.entries(props).filter(
      ([key, _val]) =>
        !RESPONSIVE_SIZES_ARR.includes(key) && !LAYOUT_PROP_ARR.includes(key),
    ),
  );

  const breakpoint = useBreakpoint();

  const getValue = (style: keyof LayoutCSSVariables) => {
    if (breakpoint?.name === "xxl") {
      const xxlValue =
        sizeProps.xxl?.[style] ??
        sizeProps.xl?.[style] ??
        sizeProps.lg?.[style] ??
        sizeProps.md?.[style] ??
        sizeProps.sm?.[style] ??
        sizeProps.xs?.[style] ??
        sizeProps.base?.[style];

      if (style.toLowerCase().endsWith("gap")) {
        return sizeToToken(xxlValue as GapSize);
      }
      return xxlValue;
    }
    if (breakpoint?.name === "xl") {
      const xlValue =
        sizeProps.xl?.[style] ??
        sizeProps.lg?.[style] ??
        sizeProps.md?.[style] ??
        sizeProps.sm?.[style] ??
        sizeProps.xs?.[style] ??
        sizeProps.base?.[style];
      if (style.toLowerCase().endsWith("gap")) {
        return sizeToToken(xlValue as GapSize);
      }
      return xlValue;
    }
    if (breakpoint?.name === "lg") {
      const lgValue =
        sizeProps.lg?.[style] ??
        sizeProps.md?.[style] ??
        sizeProps.sm?.[style] ??
        sizeProps.xs?.[style] ??
        sizeProps.base?.[style];

      if (style.toLowerCase().endsWith("gap")) {
        return sizeToToken(lgValue as GapSize);
      }
      return lgValue;
    }
    if (breakpoint?.name === "md") {
      const mdValue =
        sizeProps.md?.[style] ??
        sizeProps.sm?.[style] ??
        sizeProps.xs?.[style] ??
        sizeProps.base?.[style];
      if (style.toLowerCase().endsWith("gap")) {
        return sizeToToken(mdValue as GapSize);
      }
      return mdValue;
    }
    if (breakpoint?.name === "sm") {
      const smValue =
        sizeProps.sm?.[style] ??
        sizeProps.xs?.[style] ??
        sizeProps.base?.[style];
      if (style.toLowerCase().endsWith("gap")) {
        return sizeToToken(smValue as GapSize);
      }
      return smValue;
    }
    if (breakpoint?.name === "xs") {
      const xsValue = sizeProps.xs?.[style] ?? sizeProps.base?.[style];
      if (style.toLowerCase().endsWith("gap")) {
        return sizeToToken(xsValue as GapSize);
      }
      return xsValue;
    }

    if (style.toLowerCase().endsWith("gap")) {
      return sizeToToken(sizeProps.base?.[style] as GapSize);
    }
    return sizeProps.base?.[style];
  };

  const layoutStyleObject = LAYOUT_PROP_ARR.reduce((o, key) => {
    const value = getValue(key as keyof LayoutCSSVariables);

    if (typeof value === "undefined") return o;
    return { ...o, [key]: value };
  }, {});

  return {
    layoutStyles: layoutStyleObject,
    componentProps: componentProps as T,
  };
}
